import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import { LocationsRestrictedDockHoursInput } from "@/Shared/Locations/Inputs/LocationsRestrictedDockHoursInput";
import {
  locationsErrorMessageForHours,
  warningMessageForPickupHours,
} from "@/Shared/Locations/Validators/locationsErrorMessageForHours";
import { getRestrictionViolation } from "@/Shared/Locations/Helpers/locationsHoursOptions";
import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { DialogResult } from "@freightsimple/ui-core/Dialog/DialogResult";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import moment from "moment";
import { useState } from "react";
import { ChangePickupDateInput } from "@/Features/ChangePickupDate/Inputs/ChangePickupDateInput";
import { changePickupDateIsPickingUpByAppointment } from "@/Features/ChangePickupDate/Helpers/changePickupDateIsPickingUpByAppointment";

interface ChangePickupDateDialogProps extends DialogBaseProps {
  shipment: Shipment;
}

export function ChangePickupDateDialog(props: ChangePickupDateDialogProps) {
  const [processing, setProcessing] = useState(false);
  const [openFrom, setOpenFrom] = useState(
    props.shipment.pickupLocation.hours.openFrom,
  );
  const [openUntil, setOpenUntil] = useState(
    props.shipment.pickupLocation.hours.openUntil,
  );
  const [date, setDate] = useState(props.shipment.pickupDate);
  const [forceValidation, setForceValidation] = useState(false);
  // The success/error notice rides on top of the pickup-date dialog, exactly
  // like the legacy alert modal stacked on the legacy modal.
  const [notice, setNotice] = useState<
    { title: string; message: string; closeOnDismiss: boolean } | undefined
  >();
  const shipmentsApi = useShipmentsApi();

  // The carrier sets an appointment pickup's time with the shipper, so only the
  // date is offered and the current hours are sent back unchanged.
  const pickingUpByAppointment = changePickupDateIsPickingUpByAppointment(
    props.shipment,
  );

  const allowAfterHours = props.shipment.pickupLocation.accessorials.includes(
    "SCHEDULING_AFTER_HOURS",
  );

  const hoursErrorMessage = locationsErrorMessageForHours(
    { openFrom, openUntil },
    allowAfterHours,
    false,
  );

  const warningMessage = warningMessageForPickupHours({ openFrom, openUntil });

  const restrictions = props.shipment.selectedQuote?.restrictions;
  const restrictionError =
    restrictions && restrictions.length > 0
      ? getRestrictionViolation(
          openFrom,
          LocationContext.Pickup,
          "openFrom",
          restrictions,
          undefined,
        )
      : undefined;

  const isPickupToday = moment(props.shipment.pickupDate).isSame(
    moment(),
    "day",
  );

  async function onConfirm(): Promise<DialogResult> {
    if (
      !pickingUpByAppointment &&
      (hoursErrorMessage !== undefined || restrictionError !== undefined)
    ) {
      setForceValidation(true);
      return DialogResult.KeepOpen;
    }

    setProcessing(true);
    try {
      await shipmentsApi.requestPickupDateChange({
        requestPickupDateChangeRequest: {
          shipmentId: props.shipment.shipmentId,
          newPickupDate: date,
          newPickupHours: {
            openFrom,
            openUntil,
          },
        },
      });
      setProcessing(false);
      setNotice({
        title: "Received",
        message: pickingUpByAppointment
          ? "The pickup date has now been updated, and the change has been communicated to the carrier."
          : "The pickup date and time have now been updated, and the change has been communicated to the carrier.",
        closeOnDismiss: true,
      });
    } catch {
      setProcessing(false);
      setNotice({
        title: "Error",
        message: "Sorry, something went wrong. Our team has been alerted.",
        closeOnDismiss: false,
      });
    }
    return DialogResult.KeepOpen;
  }

  function dismissNotice() {
    const closeAfter = notice?.closeOnDismiss;
    setNotice(undefined);
    if (closeAfter) {
      props.onOpenChange?.(false);
    }
  }

  return (
    <>
      <Dialog
        open={props.open}
        onOpenChange={props.onOpenChange}
        title={
          pickingUpByAppointment
            ? "Change Pickup Date"
            : "Change Pickup Date or Hours"
        }
        description={
          pickingUpByAppointment
            ? "Select the updated date when your shipment will be ready for pickup."
            : "Select the updated date and times when your shipment will be ready for pickup."
        }
        width="medium"
        okText="Change Pickup Date"
        okLoading={processing}
        onOk={onConfirm}
      >
        <ControlPresentationProvider presentation="underlined">
          <Stack gap={8} align="start">
            {isPickupToday && (
              <Banner variant="warning">
                Pickup is currently scheduled for today. The carrier might
                charge a missed pickup fee if their truck is already dispatched
              </Banner>
            )}
            <ChangePickupDateInput
              country={props.shipment.pickupLocation.address.countryCode}
              currentPickupDate={props.shipment.pickupDate}
              date={date}
              onDateChange={function (m) {
                setDate(m.format("YYYY-MM-DD"));
              }}
            />
            {pickingUpByAppointment ? (
              <Banner variant="info">
                Pickup is by appointment, so the carrier will call to arrange
                the pickup time with you.
              </Banner>
            ) : (
              <LocationsRestrictedDockHoursInput
                openFrom={openFrom}
                openUntil={openUntil}
                setOpenFrom={setOpenFrom}
                setOpenUntil={setOpenUntil}
                context={LocationContext.Pickup}
                restrictions={props.shipment.selectedQuote?.restrictions}
                pickupOpenUntil={undefined}
                hoursErrorMessage={hoursErrorMessage}
                forceValidation={forceValidation}
                warningMessage={warningMessage}
              />
            )}
          </Stack>
        </ControlPresentationProvider>
      </Dialog>
      <Dialog
        open={notice !== undefined}
        onOpenChange={(open) => {
          if (!open) dismissNotice();
        }}
        title={notice?.title}
        footer={
          <Button primary onClick={dismissNotice}>
            OK
          </Button>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          {notice?.message}
        </ControlPresentationProvider>
      </Dialog>
    </>
  );
}
